import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Info, Cloud, Server, Sparkles, Check } from 'lucide-react';
import { FormulaBlock, CalcStep, MathSection } from '../../components/MathExplainer';

export function ImplTypeInfoModal({ showTypeInfo, setShowTypeInfo }) {
  if (!showTypeInfo) return null;
  return (
    <Dialog open={!!showTypeInfo} onOpenChange={() => setShowTypeInfo(null)}>
      <DialogContent className="max-w-lg" data-testid="impl-type-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-[#0F2040]">
            <div className={`w-10 h-10 rounded-xl ${showTypeInfo.iconBg} flex items-center justify-center`}>
              <showTypeInfo.icon className={`w-5 h-5 ${showTypeInfo.iconColor}`} />
            </div>
            {showTypeInfo.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-slate-600">
          <p>{showTypeInfo.detail.summary}</p>
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-2">Scope</h4>
            <p className="text-xs leading-relaxed">{showTypeInfo.detail.scope}</p>
          </div>
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-2">Typical Components</h4>
            <ul className="space-y-1.5">
              {showTypeInfo.detail.typical.map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs">
                  <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
            <strong>Best for:</strong> {showTypeInfo.detail.bestFor}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ComplexityLevelInfoModal({ showComplexityLevelInfo, setShowComplexityLevelInfo }) {
  if (!showComplexityLevelInfo) return null;
  return (
    <Dialog open={!!showComplexityLevelInfo} onOpenChange={() => setShowComplexityLevelInfo(null)}>
      <DialogContent className="max-w-lg" data-testid="complexity-level-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-[#0F2040]">
            <div className={`w-10 h-10 rounded-xl bg-${showComplexityLevelInfo.color}-100 flex items-center justify-center`}>
              <showComplexityLevelInfo.icon className={`w-5 h-5 text-${showComplexityLevelInfo.color}-600`} />
            </div>
            {showComplexityLevelInfo.title} Complexity
            <Badge variant="outline" className="text-xs ml-auto">{showComplexityLevelInfo.timeEstimate}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-slate-600">
          <p>{showComplexityLevelInfo.detail.summary}</p>
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-2">Characteristics</h4>
            <ul className="space-y-1.5">
              {showComplexityLevelInfo.detail.characteristics.map((item) => (
                <li key={item} className="flex items-start gap-2 text-xs">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-2">Effort Profile</h4>
            <p className="text-xs leading-relaxed">{showComplexityLevelInfo.detail.effort}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
            <strong>Risk Profile:</strong> {showComplexityLevelInfo.detail.riskProfile}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function HostingInfoModal({ showHostingInfo, setShowHostingInfo }) {
  return (
    <Dialog open={showHostingInfo} onOpenChange={setShowHostingInfo}>
      <DialogContent className="max-w-md" data-testid="hosting-modifier-modal">
        <DialogHeader>
          <DialogTitle className="text-[#0F2040]">Hosting Modifier</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-slate-600">
          <p>
            The hosting modifier reflects the additional complexity introduced by the chosen infrastructure model. 
            It applies a percentage adjustment to the overall effort and cost of the implementation.
          </p>
          <div className="space-y-3">
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50">
              <div className="flex items-center gap-3 mb-2">
                <Cloud className="w-5 h-5 text-slate-500" />
                <div className="font-semibold text-[#0F2040]">Public Cloud — No Modifier (1.0x)</div>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                A standard deployment on shared infrastructure. The environment is pre-configured and managed, 
                requiring minimal additional setup or operational overhead during implementation.
              </p>
            </div>
            <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/50">
              <div className="flex items-center gap-3 mb-2">
                <Server className="w-5 h-5 text-amber-600" />
                <div className="font-semibold text-[#0F2040]">Private Cloud — +5% Modifier (1.05x)</div>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                A dedicated hosting instance increases the complexity of environment provisioning, security configuration, 
                and ongoing instance management during implementation. This introduces additional coordination, testing, 
                and infrastructure-specific effort across most workstreams — reflected as a 5% uplift on total hours and cost.
              </p>
            </div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
            <strong>Note:</strong> The modifier applies multiplicatively across all component hours, not just the hosting setup task itself. 
            This accounts for the ripple effect of infrastructure complexity on configuration, testing, and deployment activities.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function RangeInfoModal({ showRangeInfo, setShowRangeInfo, results }) {
  return (
    <Dialog open={showRangeInfo} onOpenChange={setShowRangeInfo}>
      <DialogContent className="max-w-lg" data-testid="range-modal">
        <DialogHeader>
          <DialogTitle className="text-[#0F2040]">Estimate Range</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-slate-600">
          <p>
            The cost and effort ranges represent the <strong>likely bounds</strong> of the actual implementation, 
            based on the confidence score. A wider range signals more uncertainty; a tighter range means the estimate is more predictable.
          </p>
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-2">How the range is calculated</h4>
            <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-2">
              <p>The range is derived from the <strong>confidence score</strong> using this formula:</p>
              <div className="bg-white rounded-md px-3 py-2 font-mono text-[11px] border border-slate-200">
                <div>Variance = (100 - Confidence%) / 100</div>
                <div className="mt-1">Low Estimate = Base Estimate x (1 - Variance x 0.5)</div>
                <div>High Estimate = Base Estimate x (1 + Variance x 0.5)</div>
              </div>
              <p className="text-slate-500">This applies equally to both effort (hours) and cost ($).</p>
            </div>
          </div>
          {results && (
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-2">Your current estimate</h4>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left px-3 py-2 font-semibold text-slate-600">Metric</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-600">Low</th>
                    <th className="text-right px-3 py-2 font-semibold text-[#0F2040]">Base</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-600">High</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr>
                    <td className="px-3 py-2 font-medium">Effort</td>
                    <td className="px-3 py-2 text-right text-slate-500">{results.effort_range?.low?.toLocaleString()} hrs</td>
                    <td className="px-3 py-2 text-right font-semibold">{results.total_effort?.toLocaleString()} hrs</td>
                    <td className="px-3 py-2 text-right text-slate-500">{results.effort_range?.high?.toLocaleString()} hrs</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium">Cost</td>
                    <td className="px-3 py-2 text-right text-slate-500">${results.cost_range?.low?.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-semibold text-[#4ABFCF]">${results.total_cost?.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-slate-500">${results.cost_range?.high?.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-xs text-slate-400">
              Based on confidence of {results.confidence}% — variance of {(((100 - results.confidence) / 100) * 50).toFixed(1)}% above and below the base estimate.
            </div>
          </div>
          )}
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-2">What this means</h4>
            <ul className="space-y-1.5">
              <li className="flex items-start gap-2"><span className="text-[#4ABFCF] font-bold mt-0.5">-</span><div><strong>Low end:</strong> Best-case scenario if readiness is strong and scope remains stable.</div></li>
              <li className="flex items-start gap-2"><span className="text-[#4ABFCF] font-bold mt-0.5">-</span><div><strong>Base estimate:</strong> Most likely outcome based on current project factors.</div></li>
              <li className="flex items-start gap-2"><span className="text-[#4ABFCF] font-bold mt-0.5">-</span><div><strong>High end:</strong> Accounts for common implementation risks — scope creep, rework, dependencies.</div></li>
            </ul>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
            <strong>Tip:</strong> Improving your project readiness factors (governance, content, change readiness) will increase confidence and tighten the range — giving you and your stakeholders a more precise budget.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ConfidenceInfoModal({ showConfidenceInfo, setShowConfidenceInfo, results, modifiers }) {
  return (
    <Dialog open={showConfidenceInfo} onOpenChange={setShowConfidenceInfo}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="confidence-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5 text-[#0F2040]">
            <div className="w-8 h-8 rounded-lg bg-[#4ABFCF]/10 flex items-center justify-center shrink-0">
              <Info className="w-4 h-4 text-[#4ABFCF]" />
            </div>
            Confidence Score
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-slate-600">
          <p>
            Reflects how <strong>predictable</strong> the estimate is based on project readiness and complexity factors. 
            A higher score means less variance in the expected effort and cost ranges.
          </p>
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-2">Formula</h4>
            <FormulaBlock lines={[
              'Confidence = Base (90)',
              '  + (1 - Governance) \u00d7 8',
              '  + (1 - Content Readiness) \u00d7 8',
              '  + (1 - Client Capability) \u00d7 8',
              '  + (1 - Change Readiness) \u00d7 12',
              '  + (1 - Integration Complexity) \u00d7 10',
              '  + (1 - Org Complexity) \u00d7 8',
              '  + (1 - Timeline Pressure) \u00d7 6',
              '  - Complexity Score \u00d7 0.08',
              'Clamped to [50, 95]',
            ]} />
          </div>
          {results && (
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-2">Your Calculation</h4>
            <div className="bg-slate-50 rounded-lg p-3 space-y-1">
              <CalcStep label="Base Confidence" value="90" />
              <CalcStep label={`Governance impact (1 - ${modifiers.governance ?? 'n/a'}) \u00d7 8`} value={`${((1 - (modifiers.governance ?? 0.8)) * 8).toFixed(1)}`} />
              <CalcStep label={`Content impact (1 - ${modifiers.content_readiness ?? 'n/a'}) \u00d7 8`} value={`${((1 - (modifiers.content_readiness ?? 0.8)) * 8).toFixed(1)}`} />
              <CalcStep label={`Capability impact (1 - ${modifiers.client_capability ?? 'n/a'}) \u00d7 8`} value={`${((1 - (modifiers.client_capability ?? 0.8)) * 8).toFixed(1)}`} />
              <CalcStep label={`Change readiness impact (1 - ${modifiers.change_readiness ?? 'n/a'}) \u00d7 12`} value={`${((1 - (modifiers.change_readiness ?? 0.8)) * 12).toFixed(1)}`} />
              <CalcStep label={`Integration impact (1 - ${modifiers.integration_complexity ?? 'n/a'}) \u00d7 10`} value={`${((1 - (modifiers.integration_complexity ?? 0.8)) * 10).toFixed(1)}`} />
              <CalcStep label={`Org impact (1 - ${modifiers.org_complexity ?? 'n/a'}) \u00d7 8`} value={`${((1 - (modifiers.org_complexity ?? 0.8)) * 8).toFixed(1)}`} />
              <CalcStep label={`Timeline impact (1 - ${modifiers.timeline_pressure ?? 'n/a'}) \u00d7 6`} value={`${((1 - (modifiers.timeline_pressure ?? 0.8)) * 6).toFixed(1)}`} />
              <CalcStep label={`Complexity penalty (${results.complexity_score} \u00d7 0.08)`} value={`-${(results.complexity_score * 0.08).toFixed(1)}`} />
              <CalcStep label="= Confidence Score" value={`${results.confidence}%`} highlight bold />
            </div>
          </div>
          )}
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-2">What improves confidence:</h4>
            <ul className="space-y-1.5">
              <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">+</span> Strong governance maturity and content readiness</li>
              <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">+</span> Capable client team with implementation experience</li>
              <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">+</span> Strong change readiness (highest positive impact, \u00d712 weight)</li>
              <li className="flex items-start gap-2"><span className="text-emerald-500 mt-0.5">+</span> Low integration and organizational complexity</li>
            </ul>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
            <strong>Range:</strong> 50% (high uncertainty, wide estimate band) to 95% (very predictable, tight estimate band). 
            Neutral project factors produce a baseline of ~90%.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ComplexityInfoModal({ showComplexityInfo, setShowComplexityInfo, results, modifiers, implementationType }) {
  return (
    <Dialog open={showComplexityInfo} onOpenChange={setShowComplexityInfo}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="complexity-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5 text-[#0F2040]">
            <div className="w-8 h-8 rounded-lg bg-[#4ABFCF]/10 flex items-center justify-center shrink-0">
              <Info className="w-4 h-4 text-[#4ABFCF]" />
            </div>
            Complexity Score
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-slate-600">
          <p>
            Auto-calculated from your component selections and project factors to indicate 
            the overall <strong>implementation difficulty</strong>. The score drives risk indicators and is used in the confidence calculation.
          </p>
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-2">Score Ranges</h4>
            <div className="grid grid-cols-[auto_1fr_auto] gap-x-4 gap-y-2 items-center">
              <span className="inline-block w-3 h-3 rounded-full bg-emerald-500" />
              <span className="font-medium text-emerald-700">Simple</span>
              <span className="text-slate-400 text-xs">0 - 29</span>
              <span className="inline-block w-3 h-3 rounded-full bg-blue-500" />
              <span className="font-medium text-blue-700">Moderate</span>
              <span className="text-slate-400 text-xs">30 - 59</span>
              <span className="inline-block w-3 h-3 rounded-full bg-amber-500" />
              <span className="font-medium text-amber-700">Complex</span>
              <span className="text-slate-400 text-xs">60 - 79</span>
              <span className="inline-block w-3 h-3 rounded-full bg-red-500" />
              <span className="font-medium text-red-700">Very Complex</span>
              <span className="text-slate-400 text-xs">80 - 100</span>
            </div>
          </div>
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-2">Formula & Factors</h4>
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left px-3 py-2 font-semibold text-slate-600">Factor</th>
                    <th className="text-left px-3 py-2 font-semibold text-slate-600">Calculation</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-600">Max</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr><td className="px-3 py-2">Integrations</td><td className="px-3 py-2 text-slate-500">min(count \u00d7 10, 30)</td><td className="px-3 py-2 text-right font-medium">30</td></tr>
                  <tr><td className="px-3 py-2">Extended Config</td><td className="px-3 py-2 text-slate-500">min(count \u00d7 5, 20)</td><td className="px-3 py-2 text-right font-medium">20</td></tr>
                  <tr><td className="px-3 py-2">Org Complexity</td><td className="px-3 py-2 text-slate-500">(slider - 0.8) \u00d7 50</td><td className="px-3 py-2 text-right font-medium">30</td></tr>
                  <tr><td className="px-3 py-2">Governance</td><td className="px-3 py-2 text-slate-500">(slider - 0.8) \u00d7 40</td><td className="px-3 py-2 text-right font-medium">24</td></tr>
                  <tr><td className="px-3 py-2">Impl. Type</td><td className="px-3 py-2 text-slate-500">Corporate=0, Workforce=10, EXE=15</td><td className="px-3 py-2 text-right font-medium">15</td></tr>
                </tbody>
              </table>
            </div>
          </div>
          {results && (
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-2">Your Calculation</h4>
            <div className="bg-slate-50 rounded-lg p-3 space-y-1">
              <CalcStep label={`Org Complexity (${modifiers.org_complexity ?? 'n/a'} - 0.8) \u00d7 50`} value={(((modifiers.org_complexity ?? 0.8) - 0.8) * 50).toFixed(0)} />
              <CalcStep label={`Governance (${modifiers.governance ?? 'n/a'} - 0.8) \u00d7 40`} value={(((modifiers.governance ?? 0.8) - 0.8) * 40).toFixed(0)} />
              <CalcStep label={`Impl Type: ${implementationType}`} value={{'Corporate': '0', 'EXE_Ecommerce': '15', 'Workforce_Enablement': '10'}[implementationType] || '0'} />
              <CalcStep label="+ Integration & Extended Config points" value="(from selections)" />
              <CalcStep label="= Complexity Score" value={`${results.complexity_score} \u2192 ${results.complexity_label}`} highlight bold />
            </div>
          </div>
          )}
          <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
            <strong>Note:</strong> The score is clamped to [0, 100]. It feeds into the Confidence calculation as a penalty (score \u00d7 0.08).
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ComponentInfoModal({ showComponentInfo, setShowComponentInfo }) {
  if (!showComponentInfo) return null;
  return (
    <Dialog open={!!showComponentInfo} onOpenChange={() => setShowComponentInfo(null)}>
      <DialogContent className="max-w-lg" data-testid="component-info-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-[#0F2040]">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
              <Info className="w-5 h-5 text-[#4ABFCF]" />
            </div>
            <span>{showComponentInfo.display_name || showComponentInfo.name}</span>
            {showComponentInfo.group && (
              <Badge
                variant="secondary"
                className="ml-1 bg-[#4ABFCF]/10 text-[#0F2040] border border-[#4ABFCF]/30 text-[11px] font-medium"
                data-testid="component-info-group-badge"
              >
                {showComponentInfo.group}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-slate-600">
          {showComponentInfo.description && (
            <div>
              <h4 className="font-semibold text-[#0F2040] mb-1.5">What is this?</h4>
              <p className="text-xs leading-relaxed">{showComponentInfo.description}</p>
            </div>
          )}
          {showComponentInfo.hours_rationale && (
            <div>
              <h4 className="font-semibold text-[#0F2040] mb-1.5">Why these hours?</h4>
              <p className="text-xs leading-relaxed">{showComponentInfo.hours_rationale}</p>
            </div>
          )}
          {showComponentInfo.base_hours && (
            <div className="bg-slate-50 rounded-lg p-3">
              <h4 className="font-semibold text-[#0F2040] mb-2 text-xs">Default Hours by Complexity</h4>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-white rounded-md p-2 border border-slate-200">
                  <p className="text-[10px] text-slate-400 uppercase">Simple</p>
                  <p className="text-sm font-bold text-[#0F2040]">{showComponentInfo.base_hours.simple}h</p>
                </div>
                <div className="bg-white rounded-md p-2 border border-slate-200">
                  <p className="text-[10px] text-slate-400 uppercase">Standard</p>
                  <p className="text-sm font-bold text-[#0F2040]">{showComponentInfo.base_hours.standard}h</p>
                </div>
                <div className="bg-white rounded-md p-2 border border-slate-200">
                  <p className="text-[10px] text-slate-400 uppercase">Complex</p>
                  <p className="text-sm font-bold text-[#0F2040]">{showComponentInfo.base_hours.complex}h</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function FactorInfoModal({ showFactorInfo, setShowFactorInfo }) {
  if (!showFactorInfo) return null;
  return (
    <Dialog open={!!showFactorInfo} onOpenChange={() => setShowFactorInfo(null)}>
      <DialogContent className="max-w-lg" data-testid="factor-info-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-[#0F2040]">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#4ABFCF]" />
            </div>
            {showFactorInfo.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm text-slate-600">
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-1.5">What does this measure?</h4>
            <p className="text-xs leading-relaxed">{showFactorInfo.description}</p>
          </div>
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-1.5">Why does it matter?</h4>
            <p className="text-xs leading-relaxed">{showFactorInfo.impact}</p>
          </div>
          <div>
            <h4 className="font-semibold text-[#0F2040] mb-2">Level Descriptions</h4>
            <div className="space-y-2">
              {Object.entries(showFactorInfo.levels).map(([level, desc]) => (
                <div key={level} className="flex gap-2 text-xs">
                  <span className="font-semibold text-[#0F2040] whitespace-nowrap min-w-[80px]">{level}</span>
                  <span className="text-slate-500">{desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
